import { isNearLocation } from '../utils/geo';

const BASE_URL = import.meta.env.VITE_WEATHER_API_BASE_URL || 'https://api.open-meteo.com/v1/forecast';
const CACHE_KEY = 'dayforit_weather_cache';
const CACHE_DURATION = parseInt(import.meta.env.VITE_WEATHER_CACHE_DURATION) || 10 * 60 * 1000; // 10 minutes
// Older cache can still be *shown* instantly while fresh data loads, up to this age.
const STALE_MAX_AGE = 3 * 60 * 60 * 1000; // 3 hours
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT) || 5000;

const readCachedWeather = () => {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    return JSON.parse(cached);
};

/**
 * Cached weather, if fresh (under 10 minutes old).
 * With `allowStale`, also returns entries up to 3 hours old, for showing a verdict instantly
 * while a fresh request runs. `isFresh` says which it is.
 * @returns {{ data, location, isFresh } | null}
 */
export const getCachedWeather = ({ allowStale = false } = {}) => {
    try {
        const parsed = readCachedWeather();
        if (!parsed) return null;

        const { data, timestamp, location } = parsed;
        const age = Date.now() - timestamp;
        const isFresh = age < CACHE_DURATION;

        if (isFresh || (allowStale && age < STALE_MAX_AGE)) {
            return { data, location, isFresh };
        }
    } catch (error) {
        console.warn("Error reading cache:", error);
    }
    return null;
};

// Cache weather data
const cacheWeather = (data, latitude, longitude) => {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
            data,
            location: { latitude, longitude },
            timestamp: Date.now(),
        }));
    } catch (error) {
        console.warn("Error writing cache:", error);
    }
};

export const getWeather = async (latitude, longitude) => {
    // Check if we have cached data for this location
    const cached = getCachedWeather();
    if (cached && isNearLocation(cached.location, { latitude, longitude })) {
        return cached.data;
    }

    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);
        const params = new URLSearchParams({
            latitude: String(latitude),
            longitude: String(longitude),
            current: [
                'temperature_2m',
                'apparent_temperature',
                'relative_humidity_2m',
                'precipitation',
                'weather_code',
                'cloud_cover',
                'wind_speed_10m',
                'wind_gusts_10m',
                'uv_index',
                'is_day',
            ].join(','),
            timezone: 'auto',
        });

        const response = await fetch(`${BASE_URL}?${params.toString()}`, {
            signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
            throw new Error(`Weather request failed with status ${response.status}`);
        }

        const data = await response.json();

        // Cache the successful response
        cacheWeather(data, latitude, longitude);
        return data;
    } catch (error) {
        console.error("Error fetching weather:", error);

        try {
            const staleCached = readCachedWeather();

            if (staleCached && isNearLocation(staleCached.location, { latitude, longitude })) {
                console.warn("Using stale cached weather data");
                return staleCached.data;
            }
        } catch (cacheError) {
            console.warn("Error reading stale cache:", cacheError);
        }

        throw error;
    }
};
